import React, { PureComponent } from 'react';
import './Charts.css';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import { options } from './options';
import { getEstCelAmbLlunes, getArrowColor } from '../../../utils/utils';
import { METRES_TO_KMH } from '../../../utils/globals';

export class Charts extends PureComponent {
    state = {
        windIconsStyles: '',
    };

    chartContainer = React.createRef();

    static propTypes = {
        day: PropTypes.number,
        estCel: PropTypes.arrayOf(
            PropTypes.shape({
                data: PropTypes.string,
                valor: PropTypes.number,
            })
        ),
        figureMargin: PropTypes.func,
        location: PropTypes.string,
        mainChart: PropTypes.string,
        precAcu: PropTypes.arrayOf(
            PropTypes.shape({
                data: PropTypes.string,
                valor: PropTypes.number,
            })
        ),
        rerenderChart: PropTypes.bool,
        secondaryChart: PropTypes.bool,
        temp: PropTypes.arrayOf(
            PropTypes.shape({
                data: PropTypes.string,
                valor: PropTypes.number,
            })
        ),
        ventDir: PropTypes.arrayOf(
            PropTypes.shape({
                data: PropTypes.string,
                valor: PropTypes.number,
            })
        ),
        ventVelo: PropTypes.arrayOf(
            PropTypes.shape({
                data: PropTypes.string,
                valor: PropTypes.number,
            })
        ),
        windSpeedFormat: PropTypes.string,
    };

    render() {
        return (
            <React.Fragment>
                <style>{`${this.state.windIconsStyles}`}</style>
                <div className="mc-widget-dataviz__headers">
                    {this.createChartHeaders().map(th => (
                        <div key={th}>
                            <span>{th}</span>
                        </div>
                    ))}
                </div>
                <div
                    id="mc-widget-dataviz__container"
                    ref={this.chartContainer}
                    style={{ width: '100%', height: '121px', margin: '0' }}
                />
            </React.Fragment>
        );
    }

    componentDidMount() {
        this.printsChart();
    }

    componentDidUpdate(prevProps) {
        if (
            (this.props.rerenderChart === true &&
                this.props.estCel !== prevProps.estCel) ||
            this.props.day !== prevProps.day
        ) {
            this.chart.destroy();
            this.printsChart();
        }
    }

    // Destroy chart before unmount.
    componentWillUnmount() {
        this.chart.destroy();
    }

    printsChart = () => {
        const {
            temp,
            estCel,
            precAcu,
            ventVelo,
            mainChart,
            secondaryChart,
            figureMargin,
        } = this.props;

        // Defaults values (mainChart === 'estCel')
        let lineArray = this.propsToArray(temp);
        let iconsDataArray = this.propsToArray(estCel);

        // dynamic Y axis depending on min & max values (temp or wind speed)
        let maxValueY = Math.round(Math.max(...this.propsToArray(temp)));
        let minValueY = Math.round(
            Math.min(...this.propsToArray(temp).filter(value => value !== null))
        );

        if (mainChart === 'vent') {
            lineArray = this.propsToArrayWindSpeed(ventVelo);
            iconsDataArray = this.propsToArray(ventVelo);
            maxValueY = Math.round(
                Math.max(...this.propsToArrayWindSpeed(ventVelo))
            );
            minValueY = Math.round(
                Math.min(...this.propsToArrayWindSpeed(ventVelo))
            );
        }

        // Graph top and bottom paddings
        let range = maxValueY - minValueY;
        const paddingTop = 0.4;
        const paddingBottom = 0.6;
        maxValueY = maxValueY + range * paddingTop;
        minValueY = minValueY - range * paddingBottom;

        const lineArrayWithMarkers = this.setSerieWithIcons(
            lineArray,
            iconsDataArray
        );
        let precAcuArray = this.propsToArray(precAcu);
        precAcuArray = this.formatPrecAcuData(precAcuArray);

        const precAcuRange = 20;
        const maxValuePrecAcuY =
            Math.max(precAcuArray) > precAcuRange
                ? Math.max(precAcuArray)
                : precAcuRange;

        // Array for Background, which is a data serie in the chart
        const precAcuBackground = Array(24).fill(maxValuePrecAcuY);

        const hasPrecAcu = secondaryChart;
        const dataLabelLine = mainChart === 'estCel' ? `°` : '';

        this.chart = Highcharts.chart(
            this.chartContainer.current,
            options(
                lineArray,
                lineArrayWithMarkers,
                precAcuArray,
                maxValuePrecAcuY,
                precAcuBackground,
                maxValueY,
                minValueY,
                hasPrecAcu,
                dataLabelLine
            )
        );

        // Pass label margin to parent
        figureMargin(minValueY, maxValueY, lineArray);

        // Timeout is because we want the chart animation to finish before
        setTimeout(() => this.inlineStyles(), 700);
    };

    createChartHeaders = () => {
        const th = [
            0,
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8,
            9,
            10,
            11,
            12,
            13,
            14,
            15,
            16,
            17,
            18,
            19,
            20,
            21,
            22,
            23,
        ];
        const thWithLeftZero = th.map(hour => (hour < 10 ? `0${hour}` : hour));

        return thWithLeftZero;
    };

    propsToArray = propsData => propsData.map(data => data.valor);

    propsToArrayWindSpeed = propsData =>
        propsData.map(
            data => (data.valor !== null ? data.valor * METRES_TO_KMH : null)
        );

    setSerieWithIcons = (valuesForPositioning, valuesForIcons) => {
        const { mainChart, windSpeedFormat } = this.props;

        const data = valuesForPositioning.map((temp, i) => {
            const symbol =
                mainChart === 'estCel'
                    ? `url(${getEstCelAmbLlunes(valuesForIcons[i], i)})`
                    : `url(${getArrowColor(
                          Math.round(valuesForIcons[i] * METRES_TO_KMH),
                          'svgIcon',
                          windSpeedFormat
                      )})`;

            return {
                y: temp,
                marker: {
                    symbol: symbol,
                    width: 15,
                    height: 15,
                },
            };
        });

        return data;
    };

    formatPrecAcuData = precAcuArray => {
        const data = precAcuArray.map(data => {
            if (data === null) return null;
            return data > 0 && data < 1
                ? Math.round(data * 10) / 10
                : Math.round(data);
        });
        return data;
    };

    // For wind arrows rotation
    inlineStyles = () => {
        const { mainChart, ventDir } = this.props;
        const { series } = this.chart;
        const data = series[2].data;
        const arrowStyles = [];

        // Reset if main chart is estCel
        if (mainChart === 'estCel') {
            this.setState({ windIconsStyles: '' });
            return;
        }

        data.forEach((data, index) => {
            // Style construct
            const className = `arrow${index}`;
            const x = Math.round(data.plotX);
            const y = Math.round(data.plotY);

            const classValue = `transform: rotate(${
                ventDir[index].valor
            }deg) translate(${x - 10}px, ${y -
                20}px); transform-origin: ${x}px ${y -
                10}px; -webkit-transform: rotate(${
                ventDir[index].valor
            }deg) translate(${x - 10}px, ${y -
                20}px); -webkit-transform-origin: ${x}px ${y - 10}px`;

            // Reset markers positioning and give a classname and an id
            if (data.graphic) {
                data.graphic.attr({
                    x: 0,
                    y: 0,
                    id: className,
                    class: 'wind-arrows',
                });
            }

            arrowStyles.push(classValue);
        });

        const windIconsStyles = arrowStyles
            .map((transform, index) => `#arrow${index}{${transform}}`)
            .join('');

        this.setState({ windIconsStyles });
    };
}



// WEBPACK FOOTER //
// ./src/partials/DataViz/Charts/Charts.js